import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Target } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtNum } from "@/lib/format";
import { Prod, productLabel, fgOf } from "@/lib/fg";

const cell = { green: "bg-emerald-50 text-emerald-900", amber: "bg-amber-50 text-amber-900", red: "bg-rose-50 text-rose-900", no_capacity: "bg-slate-100 text-slate-500", none: "text-slate-400" };

export function CapacityVsPlan() {
  const q = useQuery({ queryKey: ["sales-capacity"], queryFn: async () => (await api.get("/sales/capacity")).data });
  const d = q.data;
  return (
    <div className="pp-card p-5 rise rise-2" data-testid="capacity-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="pp-label">Capacity vs plan · fixed months</div>
          <div className="text-sm text-slate-500">Capacity = lines × UPH × working hrs × working days (Customer Master). Red = shortfall, amber = ≥90% loaded.</div>
        </div>
        {d && (d.flagged ? <span className="flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700" data-testid="capacity-flagged"><AlertTriangle size={14} /> {d.flagged} shortfall(s)</span>
          : <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="capacity-flagged"><CheckCircle2 size={14} /> Plan within capacity</span>)}
      </div>
      {!d ? <div className="h-32 animate-pulse rounded bg-slate-100" /> : !d.rows.length ? <div className="text-sm text-slate-500">No active customers.</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs sm:text-sm border-collapse" data-testid="capacity-table">
            <thead><tr className="pp-label border-b border-slate-200 text-left">
              <th className="py-2 pr-3">FG Code · Customer</th><th className="py-2 px-2 text-right">Lines</th><th className="py-2 px-2 text-right">MP</th><th className="py-2 px-2 text-right">Cap/month</th>
              {d.months.map((m) => <th key={m.month} className="py-2 px-2 text-right" colSpan={2}>{m.label}<div className="text-[10px] font-normal text-slate-400">plan / need</div></th>)}
            </tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.customer_id} className="border-b border-slate-100" data-testid={`capacity-row-${r.customer_id}`}>
                  <td className="py-1.5 pr-3 font-medium whitespace-nowrap"><Prod r={r} /></td>
                  <td className="py-1.5 px-2 text-right font-mono">{r.lines_have}</td><td className="py-1.5 px-2 text-right font-mono">{r.mp_have}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-600">{fmtNum(r.lines_have * r.per_line_month)}</td>
                  {r.cells.map((x) => (<>
                    <td key={x.month} className={`py-1.5 pl-2 pr-1 text-right font-mono ${cell[x.status]}`} data-testid={`capacity-cell-${r.customer_id}-${x.month}`}>
                      {x.planned ? fmtNum(x.planned) : "—"}{x.utilisation_pct != null && x.planned ? <div className="text-[10px] opacity-70">{x.utilisation_pct}% load</div> : null}
                    </td>
                    <td key={x.month + "n"} className={`py-1.5 pl-1 pr-2 text-right font-mono text-[11px] ${cell[x.status]}`}>
                      {x.status === "no_capacity" ? "no UPH" : x.planned ? (x.status === "red" ? <span className="font-semibold">+{x.lines_short} line · +{x.mp_short} MP<div className="opacity-70">short {fmtNum(x.shortfall_qty)}</div></span> : <span>{x.lines_needed} line · {x.mp_needed} MP</span>) : ""}
                    </td>
                  </>))}
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 border-slate-300 font-mono font-semibold" data-testid="capacity-totals">
              <td className="py-2 pr-3">Total</td><td /><td /><td />
              {d.months.map((m) => { const t = d.totals[m.month]; return (<>
                <td key={m.month} className="py-2 pl-2 pr-1 text-right">{fmtNum(t.planned)}<div className="text-[10px] font-normal text-slate-500">cap {fmtNum(t.capacity)}</div></td>
                <td key={m.month + "s"} className={`py-2 pl-1 pr-2 text-right text-[11px] ${t.shortfall ? "text-rose-700" : "text-emerald-700"}`}>{t.shortfall ? `+${t.lines_short} line · +${t.mp_short} MP` : "OK"}</td>
              </>); })}
            </tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export function PlanVsActual() {
  const q = useQuery({ queryKey: ["sales-accuracy"], queryFn: async () => (await api.get("/sales/accuracy", { params: { months: 6 } })).data });
  const d = q.data; const t = d?.totals;
  return (
    <div className="pp-card p-5 rise rise-3" data-testid="accuracy-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="pp-label flex items-center gap-2"><Target size={14} /> Plan vs actual dispatch · last 6 months</div>
          <div className="text-sm text-slate-500">Forecast accuracy = 100 − |actual − plan| ÷ plan. Green ≥90%, amber ≥75%, red below.</div>
        </div>
        {t && <div className="flex gap-3 text-xs font-mono" data-testid="accuracy-summary">
          <span className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5">Planned {fmtNum(t.planned)} · Actual {fmtNum(t.actual)}{t.fill_pct != null ? ` · ${t.fill_pct}%` : ""}</span>
          <span className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5">Avg accuracy <strong>{t.avg_accuracy_pct != null ? `${t.avg_accuracy_pct}%` : "—"}</strong></span>
        </div>}
      </div>
      {!d ? <div className="h-32 animate-pulse rounded bg-slate-100" /> : !d.rows.length ? <div className="text-sm text-slate-500">No plan or dispatch data in past months yet — accuracy builds up as months close.</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs sm:text-sm border-collapse" data-testid="accuracy-table">
            <thead><tr className="pp-label border-b border-slate-200 text-left">
              <th className="py-2 pr-3">FG Code · Customer</th>
              {d.months.map((m) => <th key={m.month} className="py-2 px-2 text-right">{m.label}<div className="text-[10px] font-normal text-slate-400">plan / actual</div></th>)}
              <th className="py-2 px-2 text-right">6M fill</th><th className="py-2 px-2 text-right">Accuracy</th>
            </tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.customer_id} className="border-b border-slate-100" data-testid={`accuracy-row-${r.customer_id}`}>
                  <td className="py-1.5 pr-3 font-medium whitespace-nowrap"><Prod r={r} /></td>
                  {r.cells.map((x) => (
                    <td key={x.month} className={`py-1.5 px-2 text-right font-mono ${cell[x.status]}`}>
                      {x.status === "none" ? "—" : <>{fmtNum(x.planned)} / <strong>{fmtNum(x.actual)}</strong>{x.variance_pct != null && <div className="text-[10px] opacity-70">{x.variance >= 0 ? "+" : ""}{x.variance_pct}%</div>}</>}
                    </td>
                  ))}
                  <td className="py-1.5 px-2 text-right font-mono">{r.fill_pct != null ? `${r.fill_pct}%` : "—"}</td>
                  <td className="py-1.5 px-2 text-right">{r.avg_accuracy_pct != null ? <StatusBadge status={r.avg_accuracy_pct >= 90 ? "green" : r.avg_accuracy_pct >= 75 ? "amber" : "red"} label={`${r.avg_accuracy_pct}%`} /> : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 border-slate-300 font-mono font-semibold">
              <td className="py-2 pr-3">Total</td>
              {d.months.map((m) => { const c = t.by_month[m.month]; return <td key={m.month} className="py-2 px-2 text-right">{c.planned || c.actual ? `${fmtNum(c.planned)} / ${fmtNum(c.actual)}` : "—"}</td>; })}
              <td className="py-2 px-2 text-right">{t.fill_pct != null ? `${t.fill_pct}%` : "—"}</td><td className="py-2 px-2 text-right">{t.avg_accuracy_pct != null ? `${t.avg_accuracy_pct}%` : "—"}</td>
            </tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
